import {
  Text,
  ActivityCard,
  BorderRadius4,
  BorderRadius8,
  GreyGrey10,
  GreyGrey30,
  GreyGrey60,
  GreyGrey70,
  GreyGrey80,
  GreyGrey90,
  IconArrowRight,
  IconCalendarDays,
  IconFlag,
  IconSignature,
  IndigoIndigo60,
  Link,
  Primary13Regular,
  Primary13SemiBold,
  Primary14SemiBold,
  Box,
  Flex
} from '@sequencehq/core-components'
import { useQuery } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import Spinner from 'components/Loading'
import { useFeatureFlags } from 'hooks/useFeatureFlags'

const DISPLAY_LIMIT = 3

export const QuotesReadyToExecute = () => {
  const { showQuoteBuilder } = useFeatureFlags()

  const getLabel = (preliminaryBillingStartDate?: string) => {
    if (!preliminaryBillingStartDate) return 'Awaiting start date'
    return 'Billing set to start '
  }

  const { data: quotes, isLoading } = useQuery(
    dashboard20240730Client.getQuotes,
    {
      limit: 100,
      status: 'ACCEPTED'
    }
  )

  if (!showQuoteBuilder) return null

  const filteredQuotes = quotes?.items.filter(quote => quote.expiresAt)

  const displayedQuotes = filteredQuotes?.slice(0, DISPLAY_LIMIT) ?? []
  const remainingQuotes = filteredQuotes?.slice(DISPLAY_LIMIT).length ?? 0

  const allQuotes = filteredQuotes?.length ?? 0

  return (
    <Flex direction="column" paddingTop="24px">
      <Text textStyle="sectionHeader" {...Primary14SemiBold}>
        Start billing for
        <Text
          as="span"
          fontWeight="inherit"
          opacity={isLoading ? 0 : 1}
          transition="opacity 200ms"
        >
          {allQuotes > 99 ? ' 99+' : ` ${allQuotes}`} signed{' '}
          {allQuotes === 1 ? ' quote' : ' quotes'}
        </Text>
      </Text>

      <Flex
        direction="column"
        gap="8px"
        marginTop="12px"
        borderRadius={BorderRadius8}
        background={GreyGrey10}
        p="12px"
      >
        <Text {...Primary13SemiBold} color={GreyGrey70} paddingBottom="4px">
          Ready to execute
        </Text>

        {isLoading && (
          <Box paddingY="24px">
            <Spinner />
          </Box>
        )}

        {displayedQuotes.map(quote => (
          <ActivityCard
            key={quote.id}
            href={`/quotes/${quote.id}`}
            dateIcon={<IconFlag />}
            title={quote.title}
            date={quote.preliminaryBillingStartDate ?? ''}
            dateLabel={getLabel(quote.preliminaryBillingStartDate)}
          >
            <Flex gap="4px" alignItems="center">
              <IconSignature color={GreyGrey60} />
              <Text color={GreyGrey70} {...Primary13Regular}>
                {quote.quoteNumber}
              </Text>
            </Flex>
          </ActivityCard>
        ))}

        {!isLoading && displayedQuotes.length === 0 && (
          <Flex
            alignItems="center"
            flexDir="column"
            justifyContent="center"
            paddingY="16px"
          >
            <Box
              backgroundColor={GreyGrey30}
              borderRadius={BorderRadius4}
              width="24px"
              height="24px"
              p="4px"
            >
              <IconCalendarDays color={GreyGrey70} />
            </Box>
            <Text
              {...Primary13SemiBold}
              color={GreyGrey90}
              paddingTop="8px"
              paddingBottom="4px"
            >
              No quotes to display
            </Text>
            <Text {...Primary13Regular} color={GreyGrey80}>
              No quotes are ready to be converted into billing schedules
            </Text>
          </Flex>
        )}

        {remainingQuotes > 0 && (
          <Link
            to="/quotes"
            color="indigo.50"
            _hover={{ textDecoration: 'none' }}
            display="flex"
            gap="4px"
            paddingTop="4px"
            alignItems="center"
          >
            <Text {...Primary13SemiBold} color={IndigoIndigo60}>
              View {allQuotes > 99 ? '' : remainingQuotes} more
            </Text>
            <IconArrowRight />
          </Link>
        )}
      </Flex>
    </Flex>
  )
}
